import React, { useCallback, useState } from 'react';
import { Button, Dropdown, Slider, Tooltip } from 'antd';
import SpinInputNumber from '../SpinInputNumber';
import { useScrollDrag } from '../../hooks/useScrollDrag';
import {
  BackwardOutlined,
  StepBackwardOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import {
  useAppStore,
  ALL_PROPS,
  KeyframeProp,
  LayerKeyframes,
  EasingType,
  emptyLayerKeyframes,
} from '../../store/useAppStore';
import {
  PROP_COLOR, PROP_LABEL, FRAME_OFFSET, formatTime, SelectedLayerKf,
} from './timelineConstants';

// ── Time-string parser ─────────────────────────────────────────────
// Accepts: SS.FF   MM:SS.FF   MM:SS   SS   (FF = sub-second frames)
function parseTimeInput(str: string, fps: number): number | null {
  const s = str.trim();
  const colonMatch = s.match(/^(\d+):(\d+)(?:\.(\d+))?$/);
  if (colonMatch) {
    const mm = parseInt(colonMatch[1], 10);
    const ss = parseInt(colonMatch[2], 10);
    const ff = colonMatch[3] !== undefined ? parseInt(colonMatch[3], 10) : 0;
    return (mm * 60 + ss) * fps + ff;
  }
  const dotMatch = s.match(/^(\d+)(?:\.(\d+))?$/);
  if (dotMatch) {
    const ss = parseInt(dotMatch[1], 10);
    const ff = dotMatch[2] !== undefined ? parseInt(dotMatch[2], 10) : 0;
    return ss * fps + ff;
  }
  return null;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface TimelineControlsBarProps {
  zoom:               number;
  onZoomChange:       (z: number) => void;
  selectedKfs:        SelectedLayerKf[];
  onSetKeyframeEasing: (
    layerId: string,
    prop: KeyframeProp,
    frame: number,
    patch: Partial<{ in: EasingType; out: EasingType }>,
  ) => void;
  onClearKeyframes: () => void;
  onRippleOpacity:  () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const TimelineControlsBar: React.FC<TimelineControlsBarProps> = React.memo(({
  zoom, onZoomChange, selectedKfs, onSetKeyframeEasing,
  onClearKeyframes, onRippleOpacity,
}) => {
  const fps            = useAppStore((s) => s.fps);
  const totalFrames    = useAppStore((s) => s.totalFrames);
  const currentFrame   = useAppStore((s) => s.currentFrame);
  const isPlaying      = useAppStore((s) => s.isPlaying);
  const inPoint        = useAppStore((s) => s.inPoint);
  const outPoint       = useAppStore((s) => s.outPoint);
  const playbackMode   = useAppStore((s) => s.playbackMode);
  const autoKey        = useAppStore((s) => s.autoKey);
  const keyframeEasings = useAppStore((s) => s.keyframeEasings);

  const setCurrentFrame  = useAppStore((s) => s.setCurrentFrame);
  const setIsPlaying     = useAppStore((s) => s.setIsPlaying);
  const setTotalFrames   = useAppStore((s) => s.setTotalFrames);
  const setPlaybackMode  = useAppStore((s) => s.setPlaybackMode);
  const setAutoKey       = useAppStore((s) => s.setAutoKey);
  const setInPoint       = useAppStore((s) => s.setInPoint);
  const setOutPoint      = useAppStore((s) => s.setOutPoint);

  const displayFrame = Math.round(currentFrame);
  const totalSecs    = Math.round(totalFrames / fps);
  // ── Scroll/drag scrubbing for time and frame display boxes ────────────────
  const frameScrubOpts = { value: currentFrame, min: 0, max: totalFrames - 1, step: 1, onChange: setCurrentFrame };
  const timeScrub  = useScrollDrag<HTMLDivElement>(frameScrubOpts);
  const frameScrub = useScrollDrag<HTMLDivElement>(frameScrubOpts);
  // ── Editable frame / time state ──────────────────────────────────────────
  const [frameEditVal, setFrameEditVal] = useState<string | null>(null);
  const [timeEditVal,  setTimeEditVal]  = useState<string | null>(null);

  const commitFrameEdit = (val: string) => {
    const n = parseInt(val.trim().replace(/^f/i, ''), 10);
    if (!isNaN(n)) {
      setCurrentFrame(Math.max(0, Math.min(totalFrames - 1, n - FRAME_OFFSET)));
      setIsPlaying(false);
    }
    setFrameEditVal(null);
  };

  const commitTimeEdit = (val: string) => {
    const parsed = parseTimeInput(val, fps);
    if (parsed !== null) {
      setCurrentFrame(Math.max(0, Math.min(totalFrames - 1, Math.round(parsed))));
      setIsPlaying(false);
    }
    setTimeEditVal(null);
  };

  const editInputStyle: React.CSSProperties = {
    fontFamily: 'monospace', fontSize: 13, color: '#f5a623',
    background: '#1a1a2e', border: '1px solid #558cf4',
    borderRadius: 3, padding: '2px 4px', outline: 'none',
    width: 66, lineHeight: '18px',
  };

  // ── FPS change handler — remaps all keyframe indices ──────────────────────
  const handleFpsChange = useCallback((v: number | null) => {
    if (!v) return;
    const newFps = v;
    const oldFps = useAppStore.getState().fps;
    if (newFps === oldFps) return;
    const durationSecs    = useAppStore.getState().totalFrames / oldFps;
    const newTotalFrames  = Math.round(durationSecs * newFps);
    const newCurrentFrame = Math.round(useAppStore.getState().currentFrame / oldFps * newFps);
    const oldKeyframes    = useAppStore.getState().layerKeyframes;
    const remapped: typeof oldKeyframes = {};
    for (const layerId of Object.keys(oldKeyframes)) {
      const lk = oldKeyframes[layerId] as LayerKeyframes;
      const newLk = emptyLayerKeyframes();
      for (const prop of ALL_PROPS) {
        const src = lk[prop] as Record<number, any>;
        const dst: Record<number, any> = {};
        for (const frameStr of Object.keys(src)) {
          const oldFrame = Number(frameStr);
          const newFrame = Math.round(oldFrame / oldFps * newFps);
          dst[newFrame] = src[oldFrame];
        }
        (newLk as any)[prop] = dst;
      }
      remapped[layerId] = newLk;
    }
    useAppStore.setState({
      fps: newFps,
      totalFrames: newTotalFrames,
      currentFrame: newCurrentFrame,
      layerKeyframes: remapped,
    });
  }, []);

  // ── Easing selection helpers ───────────────────────────────────────────────
  const selectionEasing = (side: 'in' | 'out'): EasingType | null => {
    if (!selectedKfs.length) return null;
    const vals = selectedKfs
      .map((k) => keyframeEasings[k.layerId]?.[k.prop as KeyframeProp]?.[k.frame]?.[side] ?? 'linear');
    if (!vals.length || !vals.every((v) => v === vals[0])) return null;
    return vals[0];
  };

  const applyEasing = (type: EasingType, side: 'in' | 'out' | 'both') => {
    selectedKfs
      .forEach((k) => {
        const patch = side === 'both' ? { in: type, out: type } : { [side]: type };
        onSetKeyframeEasing(k.layerId, k.prop as KeyframeProp, k.frame, patch);
      });
  };

  const hasSelection  = selectedKfs.length > 0;
  const dominantIn    = selectionEasing('in');
  const dominantOut   = selectionEasing('out');
  const dominantBoth: EasingType | null = dominantIn === dominantOut ? dominantIn : null;

  const INTERP_OPTIONS: { key: EasingType; tip: string }[] = [
    { key: 'stepped', tip: 'Hold — value jumps at next keyframe' },
    { key: 'linear',  tip: 'Linear — constant rate of change' },
    { key: 'bezier',  tip: 'Ease — smooth acceleration / deceleration' },
  ];

  const Shape = ({ type, size = 6, dimmed = false }: { type: EasingType; size?: number; dimmed?: boolean }) => (
    <div style={{
      width: size, height: size, flexShrink: 0,
      background: dimmed ? '#444' : '#aaa',
      borderRadius: type === 'bezier' ? '50%' : '1.5px',
      transform: `rotate(${type === 'stepped' ? '0' : '45'}deg)`,
      display: 'inline-block',
    }} />
  );

  const btnBase: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 18, cursor: hasSelection ? 'pointer' : 'default',
    userSelect: 'none', opacity: hasSelection ? 1 : 0.3,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderBottom: '1px solid #2a2a40', flexShrink: 0 }}>
      <Tooltip title="Rewind">
        <Button type="text" size="small" icon={<BackwardOutlined />} style={{ color: '#ccc' }}
          onClick={() => { setCurrentFrame(0); setIsPlaying(false); }} />
      </Tooltip>
      <Tooltip title="Prev frame">
        <Button type="text" size="small" icon={<StepBackwardOutlined />} style={{ color: '#ccc' }}
          onClick={() => { setIsPlaying(false); setCurrentFrame(Math.max(0, displayFrame - 1)); }} />
      </Tooltip>
      <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
        <Button type="text" size="small"
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          style={{ color: '#f5a623', fontSize: 20 }}
          onClick={() => setIsPlaying(!isPlaying)} />
      </Tooltip>
      <Tooltip title="Next frame">
        <Button type="text" size="small" icon={<StepForwardOutlined />} style={{ color: '#ccc' }}
          onClick={() => { setIsPlaying(false); setCurrentFrame(Math.min(totalFrames - 1, displayFrame + 1)); }} />
      </Tooltip>

      {/* ── Playback mode toggle ── */}
      <div style={{ display: 'flex', borderRadius: 3, border: '1px solid #2a2a40', overflow: 'hidden', marginLeft: 4, flexShrink: 0 }}>
        {(
          [{ v: 'once',     label: '⇥', tip: 'Play once (stop at end)' },
           { v: 'loop',     label: '↺',   tip: 'Loop' },
           { v: 'pingpong', label: '⇄',   tip: 'Ping-pong' }] as const
        ).map(({ v, label, tip }) => (
          <Tooltip key={v} title={tip}>
            <div
              onClick={() => setPlaybackMode(v)}
              style={{
                padding: '1px 6px', fontSize: 12, cursor: 'pointer', lineHeight: '18px',
                userSelect: 'none',
                background: playbackMode === v ? 'rgba(245,166,35,0.18)' : 'transparent',
                color: playbackMode === v ? '#f5a623' : '#444',
                borderRight: v !== 'pingpong' ? '1px solid #2a2a40' : 'none',
              }}
            >
              {label}
            </div>
          </Tooltip>
        ))}
      </div>

      {/* ── In/Out point controls ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, flexShrink: 0 }}>
        <Tooltip title={inPoint !== null ? `In: f${inPoint + FRAME_OFFSET} — click to clear (Shift+I)` : 'Set in point at playhead (I)'}>
          <div
            onClick={() => inPoint !== null ? setInPoint(null) : setInPoint(displayFrame)}
            style={{
              padding: '1px 5px', fontSize: 11, cursor: 'pointer', lineHeight: '18px',
              userSelect: 'none', borderRadius: 3, border: '1px solid #2a2a40',
              background: inPoint !== null ? 'rgba(34,197,94,0.15)' : 'transparent',
              color: inPoint !== null ? '#22c55e' : '#444',
            }}
          >
            {inPoint !== null ? `I:${inPoint + FRAME_OFFSET}` : 'I'}
          </div>
        </Tooltip>
        <Tooltip title={outPoint !== null ? `Out: f${outPoint + FRAME_OFFSET} — click to clear (Shift+O)` : 'Set out point at playhead (O)'}>
          <div
            onClick={() => outPoint !== null ? setOutPoint(null) : setOutPoint(displayFrame)}
            style={{
              padding: '1px 5px', fontSize: 11, cursor: 'pointer', lineHeight: '18px',
              userSelect: 'none', borderRadius: 3, border: '1px solid #2a2a40',
              background: outPoint !== null ? 'rgba(248,113,113,0.15)' : 'transparent',
              color: outPoint !== null ? '#f87171' : '#444',
            }}
          >
            {outPoint !== null ? `O:${outPoint + FRAME_OFFSET}` : 'O'}
          </div>
        </Tooltip>
      </div>

      <div style={{ fontSize: 11, color: selectedKfs.length ? '#58a6ff' : '#555', marginLeft: 8 }}>
        Sel {selectedKfs.length}
      </div>

      {/* ── Interpolation buttons for selection ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 8, borderRadius: 3, border: '1px solid #2a2a40', overflow: 'hidden', flexShrink: 0 }}>
        {INTERP_OPTIONS.map(({ key, tip }) => {
          const active = dominantBoth === key || (dominantIn === key && dominantOut === key);
          return (
            <Tooltip key={key} title={hasSelection ? tip : `${tip} (select keyframes first)`} placement="bottom">
              <div
                style={{
                  ...btnBase,
                  background: active ? 'rgba(245,166,35,0.15)' : 'transparent',
                  borderRight: key !== 'bezier' ? '1px solid #2a2a40' : 'none',
                }}
                onClick={() => hasSelection && applyEasing(key, 'both')}
              >
                <Shape type={key} dimmed={!active} />
              </div>
            </Tooltip>
          );
        })}
      </div>

      
      {/* ── Auto-key toggle ── */}
      <div style={{ display: 'flex', borderRadius: 3, border: '1px solid #2a2a40', overflow: 'hidden', marginLeft: 8, flexShrink: 0 }}>
        {(
          [{ v: 0, label: 'MK',  tip: 'Manual key' },
           { v: 1, label: 'AK', tip: 'Auto-key if channel has keyframes' },
           { v: 2, label: 'AK+', tip: 'Auto-key all channels on change' }] as const
        ).map(({ v, label, tip }) => (
          <Tooltip key={v} title={tip}>
            <div
              onClick={() => setAutoKey(autoKey === v ? 0 : v)}
              style={{
                padding: '1px 6px', fontSize: 10, cursor: 'pointer', lineHeight: '18px',
                userSelect: 'none',
                background: autoKey === v
                  ? (v === 1 ? 'rgba(245,166,35,0.15)' : v === 2 ? 'rgba(255,77,79,0.15)' : '#1a1a2e')
                  : 'transparent',
                color: autoKey === v
                  ? (v === 0 ? '#555' : v === 1 ? '#f5a623' : '#ff4d4f')
                  : '#444',
                borderRight: v < 2 ? '1px solid #2a2a40' : 'none',
              }}
            >
              {label}
            </div>
          </Tooltip>
        ))}
      </div>

      <div style={{ flex: 1 }} />

      {/* ── Tools menu ── */}
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'clear',
              label: 'Clear Keyframes',
              onClick: onClearKeyframes,
            },
            {
              key: 'ripple',
              label: 'Ripple Layer Start',
              onClick: onRippleOpacity,
            },
          ],
        }}
      >
        <div style={{
          padding: '1px 7px', fontSize: 11, cursor: 'pointer', lineHeight: '18px',
          userSelect: 'none', borderRadius: 3, border: '1px solid #2a2a40',
          color: '#888', marginRight: 4,
        }}>
          Tools ▾
        </div>
      </Dropdown>

      {/* Zoom */}
      <Tooltip title="Zoom out">
        <Button type="text" size="small" icon={<MinusOutlined />} style={{ color: '#888' }}
          onClick={() => onZoomChange(Math.max(1, zoom / 1.5))} />
      </Tooltip>
      <Slider min={1} max={20} step={0.5} value={zoom} onChange={(v) => onZoomChange(v as number)}
        style={{ width: 56, margin: 0 }} tooltip={{ formatter: (v) => `${v}x` }} />
      <Tooltip title="Zoom in">
        <Button type="text" size="small" icon={<PlusOutlined />} style={{ color: '#888' }}
          onClick={() => onZoomChange(Math.min(20, zoom * 1.5))} />
      </Tooltip>

      <span style={{ fontSize: 11, color: '#555', marginLeft: 6 }}>FPS</span>
      <SpinInputNumber size="small" min={1} max={120} value={fps} style={{ width: 52 }}
        onChange={handleFpsChange} />
      <span style={{ fontSize: 11, color: '#555', marginLeft: 4 }}>Length(sec)</span>
      <SpinInputNumber size="small" min={1} max={3600} value={totalSecs} style={{ width: 58 }}
        onChange={(v) => v && setTotalFrames(v * fps)} />

      {timeEditVal !== null ? (
        <input
          autoFocus
          value={timeEditVal}
          onChange={(e) => setTimeEditVal(e.target.value)}
          onBlur={(e) => commitTimeEdit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            else if (e.key === 'Escape') setTimeEditVal(null);
            e.stopPropagation();
          }}
          style={editInputStyle}
        />
      ) : (
        <Tooltip title="Click to jump to time (SS.FF or MM:SS.FF)">
          <div
            ref={timeScrub.ref}
            onPointerDown={timeScrub.onPointerDown}
            onPointerMove={timeScrub.onPointerMove}
            onPointerUp={timeScrub.onPointerUp}
            onClick={() => setTimeEditVal(formatTime(displayFrame, fps))}
            style={{ fontFamily: 'monospace', fontSize: 13, color: '#f5a623', minWidth: 50, borderRadius: 3, border: '1px solid rgb(42, 42, 64)', padding: '4px 4px', cursor: 'text', userSelect: 'none' }}
          >
            {formatTime(displayFrame, fps)}
          </div>
        </Tooltip>
      )}
      {frameEditVal !== null ? (
        <input
          autoFocus
          value={frameEditVal}
          onChange={(e) => setFrameEditVal(e.target.value)}
          onBlur={(e) => commitFrameEdit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            else if (e.key === 'Escape') setFrameEditVal(null);
            e.stopPropagation();
          }}
          style={editInputStyle}
        />
      ) : (
        <Tooltip title="Click to jump to frame">
          <div
            ref={frameScrub.ref}
            onPointerDown={frameScrub.onPointerDown}
            onPointerMove={frameScrub.onPointerMove}
            onPointerUp={frameScrub.onPointerUp}
            onClick={() => setFrameEditVal(String(displayFrame + FRAME_OFFSET))}
            style={{ fontFamily: 'monospace', fontSize: 13, color: '#f5a623', minWidth: 50, borderRadius: 3, border: '1px solid rgb(42, 42, 64)', padding: '4px 4px', cursor: 'text', userSelect: 'none' }}
          >
            f{displayFrame + FRAME_OFFSET}
          </div>
        </Tooltip>
      )}

    </div>
  );
});

export default TimelineControlsBar;
